<template>
  <v-container
    fluid
    class="fill-height"
  >
    <v-row
      class="ma-0"
    >
      <v-col
        v-if="!savedCreditCardIsEmpty && isPayableWithCreditCard"
        cols="12"
      >
        <v-subheader class="pa-0">
          <span class="title">Método de pagamento salvo</span>
        </v-subheader>
        <v-card
          width="100%"
          height="80px"
        >
          <v-row
            align="center"
            class="fill-height ma-0 px-4"
          >
            <v-radio-group
              v-model="paymentMethod"
              :error-messages="paymentMethodErrors"
            >
              <v-radio
                :value="paymentMethods.SAVED_CREDIT_CARD"
                :disabled="isLoading"
                color="accent"
                @change="onSavedCreditCardSelected"
              >
                <template v-slot:label>
                  <div :class="['mr-2', savedCreditCardLogo]"></div>
                  {{savedCreditCard | savedCreditCardLastDisplay}}
                </template>
              </v-radio>
            </v-radio-group>
          </v-row>
        </v-card>
      </v-col>
      <v-col
        cols="12"
      >
        <v-subheader class="pa-0">
          <span class="title">Novo método de pagamento</span>
        </v-subheader>
      </v-col>
      <v-col
        cols="12"
      >
        <v-card
          v-if="isPayableWithCreditCard"
          width="100%"
          height="80px"
        >
          <v-row
            align="center"
            class="fill-height ma-0 px-4"
          >
            <v-radio-group
              v-model="paymentMethod"
              :error-messages="paymentMethodErrors"
            >
              <v-radio
                :value="paymentMethods.CREDIT_CARD"
                :disabled="isLoading"
                color="accent"
                @change="onCreditCardSelected"
              >
                <template v-slot:label>
                  <v-icon left>mdi-credit-card</v-icon>
                  Cartão de crédito
                </template>
              </v-radio>
            </v-radio-group>
            <v-spacer/>
            <v-flex
              class="d-flex justify-end"
            >
              <div class="mx-1 card-logo small mastercard"></div>
              <div class="mx-1 card-logo small visa"></div>
              <div class="mx-1 card-logo small elo"></div>
              <div class="mx-1 card-logo small amex"></div>
            </v-flex>
          </v-row>
        </v-card>
        <credit-card-data
          v-if="isPayableWithCreditCard"
          v-show="paymentMethodIsCreditCard"
          v-model="strCcBrand"
          ref="credit-card-form"
          @complete="onCreditCardFormComplete"
        />
      </v-col>
      <v-col
        cols="12"
      >
        <v-card
          v-if="isPayableWithPostPay"
          width="100%"
          height="80px"
        >
          <v-row
            align="center"
            class="fill-height ma-0 px-4"
          >
            <v-radio-group
              v-model="paymentMethod"
              :error-messages="paymentMethodErrors"
            >
              <v-radio
                :value="paymentMethods.POST_PAY"
                :disabled="isLoading"
                color="accent"
                @change="onPostPaySelected"
              >
                <template v-slot:label>
                  <v-icon left>mdi-barcode</v-icon>
                  Boleto bancário
                </template>
              </v-radio>
            </v-radio-group>
          </v-row>
        </v-card>
      </v-col>
      <installments-info
        v-model="intInstallments"
        :str-cc-brand="strCcBrand"
      />
    </v-row>
    <v-row
      class="ma-0 my-2"
    >
      <v-divider/>
    </v-row>
    <v-row
      class="ma-0"
    >
      <v-btn
        color="accent"
        outlined
        :disabled="isLoading"
        @click="this.LastStep"
        :loading="PaymentMixin.isPaying"
      >
        Voltar
      </v-btn>
      <v-spacer/>
      <v-btn
        color="primary"
        @click="onNextClick"
        :loading="isLoading || PaymentMixin.isPaying"
      >
        Próximo
      </v-btn>
    </v-row>
  </v-container>
</template>

<script>
  import {CREDIT_CARD_LOGO, PAYABLE_WITH, PAYMENT_METHOD, PAYMENT_STEPS} from "../../helpers/payment/constants"
  import {isEmpty, isEqual, isNull, isUndefined} from "lodash/lang"
  import CreditCardData from "./CreditCard/CreditCardData"
  import {validationMixin} from "vuelidate"
  import required from "vuelidate/lib/validators/required"
  import {BasePayment, TokenCCPayment} from "../../helpers/payment/models"
  import {listCreditCards} from "../../api/api-billing"
  import {find, includes} from "lodash/collection"
  import InstallmentsInfo from "./InstallmentsInfo"

  const PaymentLastStep = Symbol.for("PaymentLastStep")
  const PaymentNextStep = Symbol.for("PaymentNextStep")
  const PaymentSetStep = Symbol.for("PaymentSetStep")
  const SetPaymentData = Symbol.for("SetPaymentData")
  const SetPaymentMethod = Symbol.for("SetPaymentMethod")
  const _PaymentMixin = Symbol.for("_PaymentMixin")
  const SetResumeCardData = Symbol.for("SetResumeCardData")

  export default {
    name: "PaymentMethod",
    mixins: [validationMixin],
    inject: {LastStep: PaymentLastStep, NextStep: PaymentNextStep, SetStep: PaymentSetStep, SetPaymentData, SetPaymentMethod, _PaymentMixin, SetResumeCardData},
    components: {InstallmentsInfo, CreditCardData},
    data: () => ({
      isLoading: false,

      paymentMethod: null,

      savedCreditCard: null,

      intInstallments: 1,

      strCcBrand: null,
    }),
    validations() {
      return {
        paymentMethod: {
          required
        }
      }
    },
    methods: {
      onNextClick() {
        this.$v.$touch()
        if (this.$v.$invalid) return

        if (isEqual(this.paymentMethod, PAYMENT_METHOD.CREDIT_CARD)) {
          this.isLoading = true
          this.$refs["credit-card-form"].validateCardData()
          return
        } else if (isEqual(this.paymentMethod, PAYMENT_METHOD.SAVED_CREDIT_CARD)) {
          this.SetPaymentData(new BasePayment(this.intInstallments))
          this.SetStep(PAYMENT_STEPS.NFE_INFO)
          return
        }

        this.NextStep()
      },
      onCreditCardFormComplete(creditCardData) {
        creditCardData["intInstallments"] = this.intInstallments
        this.SetPaymentData(new TokenCCPayment(creditCardData))
        this.isLoading = false
        this.NextStep()
      },

      onSavedCreditCardSelected() {
        this.strCcBrand = this.savedCreditCard['str_brand']
        this.SetPaymentMethod(PAYMENT_METHOD.SAVED_CREDIT_CARD)
        const resumeCardData = {
          strLastDigits: this.savedCreditCard["str_masked_number"].substr(this.savedCreditCard["str_masked_number"].length - 4),
          strCcBrand: this.savedCreditCard['str_brand']
        }
        this.SetResumeCardData(resumeCardData)
      },
      onCreditCardSelected() {
        this.strCcBrand = this.$refs["credit-card-form"].creditCardBrand
        this.SetPaymentMethod(PAYMENT_METHOD.CREDIT_CARD)
      },
      onPostPaySelected() {
        this.strCcBrand = null
        this.SetPaymentMethod(PAYMENT_METHOD.POST_PAY)
      }
    },
    filters: {
      savedCreditCardLastDisplay(savedCreditCard) {
        const lastDigits = savedCreditCard["str_masked_number"].substr(savedCreditCard["str_masked_number"].length - 4)
        return `**** **** **** ${lastDigits}`
      }
    },
    computed: {
      savedCreditCardIsEmpty() {
        return isNull(this.savedCreditCard) || isUndefined(this.savedCreditCard)
      },
      savedCreditCardLogo() {
        if (!this.savedCreditCard) return "card-logo small unknown"
        return CREDIT_CARD_LOGO[this.savedCreditCard['str_brand']] || "card-logo small unknown"
      },
      paymentMethodErrors() {
        const errors = []
        if (!this.$v.paymentMethod.$dirty) return errors
        !this.$v.paymentMethod.required && errors.push("É necessário selecionar algum método de pagamento")
        return errors
      },
      paymentMethodIsCreditCard() {
        return isEqual(this.paymentMethod, PAYMENT_METHOD.CREDIT_CARD)
      },
      paymentMethods() {
        return PAYMENT_METHOD
      },
      isPayableWithPostPay() {
        if (!this.PaymentMixin.shoppingCart) return false
        const abPayableWith = this.PaymentMixin.shoppingCart["ab_payable_with"]
        return includes([PAYABLE_WITH.ALL, PAYABLE_WITH.POST_PAY], abPayableWith)
      },
      isPayableWithCreditCard() {
        if (!this.PaymentMixin.shoppingCart) return false
        const abPayableWith = this.PaymentMixin.shoppingCart["ab_payable_with"]
        return includes([PAYABLE_WITH.ALL, PAYABLE_WITH.CREDIT_CARD], abPayableWith)
      },
      PaymentMixin() {
        return this._PaymentMixin()
      }
    },
    mounted() {
      listCreditCards(this.$apiAuth.getAuthToken())
        .then(res => {
          const creditCards = res.data.data
          if (isEmpty(creditCards)) return

          this.savedCreditCard = find(creditCards, item => item["bl_active"])
        })
    }
  }
</script>

<style scoped>

</style>
